/**
 * Statement Modal Mixin
 *
 * Shared state and methods for the "Generate Statement" modal,
 * used by both landlordSlideover and landlordAccountSlideover.
 *
 * Includes MTD (Making Tax Digital) quarterly period presets in both
 * HMRC bases — standard (6th–5th) and calendar-election quarters. The
 * tax year always spans 6 Apr – 5 Apr, so calendar Q1 starts 6 Apr and
 * calendar Q4 ends 5 Apr.
 */

function pad(n) {
  return String(n).padStart(2, '0')
}

function iso(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`
}

// MTD for Income Tax went live 6 April 2026 — quarters before tax year
// 2026/27 don't exist as MTD periods, so never offer them.
const MTD_FIRST_TAX_YEAR = 2026

// Quarter date windows for a tax year (identified by its starting year).
// Calendar-election quarters run 1 Apr – 31 Mar, except MTD's first year
// (2026/27) where Q1 starts 6 Apr — digital records begin at the MTD start
// date, and 1–5 Apr 2026 belongs to the 2025-26 self-assessment return.
function mtdQuartersFor(taxYearStart, basis) {
  const y = taxYearStart
  const calendarYearStart = iso(y, 4, y === MTD_FIRST_TAX_YEAR ? 6 : 1)
  const windows = basis === 'calendar'
    ? [
        { from: calendarYearStart, to: iso(y, 6, 30) },
        { from: iso(y, 7, 1), to: iso(y, 9, 30) },
        { from: iso(y, 10, 1), to: iso(y, 12, 31) },
        { from: iso(y + 1, 1, 1), to: iso(y + 1, 3, 31) },
      ]
    : [
        { from: iso(y, 4, 6), to: iso(y, 7, 5) },
        { from: iso(y, 7, 6), to: iso(y, 10, 5) },
        { from: iso(y, 10, 6), to: iso(y + 1, 1, 5) },
        { from: iso(y + 1, 1, 6), to: iso(y + 1, 4, 5) },
      ]

  const yearLabel = `${y}/${String(y + 1).slice(-2)}`
  return windows.map((w, i) => ({
    label: `Q${i + 1} ${yearLabel}`,
    from: w.from,
    to: w.to,
    ytdFrom: basis === 'calendar' ? calendarYearStart : iso(y, 4, 6),
  }))
}

export function statementModalMixin() {
  // Build tax year presets based on current date
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() + 1 // 1-indexed
  const currentDay = today.getDate()

  // Tax year starts 6 April. If before 6 April, we're still in the previous tax year.
  const inNewTaxYear = currentMonth > 4 || (currentMonth === 4 && currentDay >= 6)
  const currentTaxYearStart = inNewTaxYear ? currentYear : currentYear - 1

  function taxYearFrom(startYear) {
    return `${startYear}-04-06`
  }
  function taxYearTo(startYear) {
    return `${startYear + 1}-04-05`
  }
  function taxYearLabel(startYear) {
    return `${startYear}/${String(startYear + 1).slice(-2)}`
  }

  // Last 12 months
  const twelveMonthsAgo = new Date(today)
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1)
  const last12From = twelveMonthsAgo.toISOString().slice(0, 10)
  const todayStr = today.toISOString().slice(0, 10)

  // Last completed tax year and the one before
  const lastCompletedStart = currentTaxYearStart - 1
  const previousStart = currentTaxYearStart - 2

  // Default to the most recent completed tax year
  const defaultFrom = taxYearFrom(lastCompletedStart)
  const defaultTo = taxYearTo(lastCompletedStart)

  return {
    // State
    statementModalOpen: false,
    statementFromDate: defaultFrom,
    statementToDate: defaultTo,
    statementGenerating: false,
    statementBasis: 'standard',

    statementPresets: [
      { label: `Tax year ${taxYearLabel(lastCompletedStart)}`, from: taxYearFrom(lastCompletedStart), to: taxYearTo(lastCompletedStart) },
      { label: `Tax year ${taxYearLabel(previousStart)}`, from: taxYearFrom(previousStart), to: taxYearTo(previousStart) },
      { label: 'Last 12 months', from: last12From, to: todayStr },
    ],

    // MTD quarters for the selected basis: every quarter since MTD began
    // (April 2026) that has started, newest first, capped at 4. The current
    // quarter is included and flagged in-progress so agents can preview
    // figures to date before the period completes.
    // Methods, not getters — these are spread into the slideover components,
    // and object spread would freeze a getter to its init-time value.
    mtdQuarterPresets() {
      const quarters = []
      for (let y = MTD_FIRST_TAX_YEAR; y <= currentTaxYearStart; y++) {
        quarters.push(...mtdQuartersFor(y, this.statementBasis))
      }
      return quarters
        .filter(q => q.from <= todayStr)
        .map(q => ({ ...q, inProgress: q.to > todayStr }))
        .sort((a, b) => (a.to < b.to ? 1 : -1))
        .slice(0, 4)
    },

    // The MTD quarter matching the currently selected dates, if any. Drives
    // chip highlighting and whether the cumulative YTD section is requested —
    // manually edited dates simply stop matching, no stale state to clear.
    activeMtdQuarter() {
      return this.mtdQuarterPresets().find(q => q.from === this.statementFromDate && q.to === this.statementToDate)
    },

    isActiveMtdQuarter(quarter) {
      return quarter.from === this.statementFromDate && quarter.to === this.statementToDate
    },

    openStatementModal() {
      this.statementFromDate = defaultFrom
      this.statementToDate = defaultTo
      this.statementGenerating = false
      this.statementBasis = this.landlord?.mtd_quarter_basis || 'standard'
      this.statementModalOpen = true
    },

    closeStatementModal() {
      this.statementModalOpen = false
      this.statementGenerating = false
    },

    selectMtdQuarter(quarter) {
      this.statementFromDate = quarter.from
      this.statementToDate = quarter.to
    },

    statementUrl(format) {
      let url = `/accounting/accounts/statement.${format}?payable_type=Landlord&payable_id=${this.landlordId}&from_date=${this.statementFromDate}&to_date=${this.statementToDate}`
      const quarter = this.activeMtdQuarter()
      if (quarter) {
        url += `&ytd_from_date=${quarter.ytdFrom}`
      }
      return url
    },

    generateStatement(format = 'pdf') {
      if (!this.statementFromDate || !this.statementToDate || !this.landlordId) return

      this.statementGenerating = true
      window.open(this.statementUrl(format), '_blank')

      // Reset after a short delay (PDF opens in new tab)
      setTimeout(() => {
        this.statementGenerating = false
        this.statementModalOpen = false
      }, 1000)
    }
  }
}
